// src/Map.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import geoData from "./austria.geo.json";
import neighborsData from "./neighbors.geo.json";
import { FLAG_COLORS } from "./flagColors";

// Leaflet Icon Fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

/** Gradients für Flaggen in <defs> */
function UseCountryGradients() {
  const [mapStyle, setMapStyle] = useState("dark");
  const map = useMap();
  const injected = useRef(false);

  useEffect(() => {
    if (injected.current) return;
    const pane = map.getPanes().overlayPane;
    if (!pane) return;

    const tryInject = () => {
      const svg = pane.querySelector("svg");
      if (!svg) return false;

      const NS = "http://www.w3.org/2000/svg";
      let defs = svg.querySelector("defs");
      if (!defs) {
        defs = document.createElementNS(NS, "defs");
        svg.prepend(defs);
      }

      Object.entries(FLAG_COLORS).forEach(([iso, [c1, c2, c3]]) => {
        if (svg.querySelector(`#flag-${iso}`)) return;
        const lg = document.createElementNS(NS, "linearGradient");
        lg.setAttribute("id", `flag-${iso}`);
        lg.setAttribute("x1", "0%");
        lg.setAttribute("y1", "0%");
        lg.setAttribute("x2", "0%");
        lg.setAttribute("y2", "100%");
        [
          ["0%", c1],
          ["33.3333%", c1],
          ["33.3334%", c2],
          ["66.6666%", c2],
          ["66.6667%", c3],
          ["100%", c3],
        ].forEach(([offset, color]) => {
          const s = document.createElementNS(NS, "stop");
          s.setAttribute("offset", offset);
          s.setAttribute("stop-color", color);
          lg.appendChild(s);
        });
        defs.appendChild(lg);
      });

      injected.current = true;
      return true;
    };

    if (!tryInject()) {
      const t = setTimeout(tryInject, 0);
      return () => clearTimeout(t);
    }
  }, [map]);

  return null;
}

/** Österreich-Füllung je Modus */
function applyAustriaFill(geoLayer, mode /* 'border' | 'flags' */) {
  if (!geoLayer) return;
  geoLayer.eachLayer((layer) => {
    const el = layer.getElement?.();
    if (!el) return;
    if (mode === "flags") {
      el.setAttribute("fill", "url(#flag-AUT)");
      el.setAttribute("fill-opacity", "1");
    } else {
      el.setAttribute("fill", "none");
      el.setAttribute("fill-opacity", "0");
    }
    el.setAttribute("vector-effect", "non-scaling-stroke");
  });
}

// Name→ISO Fallback
const NAME_TO_ISO = {
  Austria: "AUT",
  Germany: "DEU",
  Switzerland: "CHE",
  Italy: "ITA",
  Slovenia: "SVN",
  Hungary: "HUN",
  "Czech Republic": "CZE",
  Czechia: "CZE",
  Slovakia: "SVK",
  Liechtenstein: "LIE",
  Spain: "ESP",
  France: "FRA",
};

// sehr grobe Durchschnittswerte in kg CO₂ pro km (pro Person)
const CO2_FACTORS = {
  zug: 0.04,
  ubahn: 0.05,
  sbahn: 0.05,
  bus: 0.09,
  fuss: 0.0,
  fahrrad: 0.0, // 🚲 emissionsfrei
  bergbahn: 0.06, // 🚠 elektrisch, realistisch
  auto: 0.18,
  schiff: 0.25,
  flug: 0.23,
};

// Emoji Marker
const makeEmojiIcon = (sizePx = 48) =>
  L.divIcon({
    className: "tree-emoji",
    html: `<div style="
      font-size:${sizePx}px;line-height:1;
      display:flex;align-items:center;justify-content:center;
      width:${sizePx}px;height:${sizePx}px;
    ">🎄</div>`,
    iconSize: [sizePx, sizePx],
    iconAnchor: [sizePx / 2, sizePx / 2],
  });

/** ---------- Statistik Kreise ---------- **/
function CircularProgress({ value, max, size = 56, stroke = 8, label }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const offset = C * (1 - pct / 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,.15)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="white"
          strokeLinecap="round"
          strokeWidth={stroke}
          strokeDasharray={`${C} ${C}`}
          strokeDashoffset={offset}
          style={{
            transition: "stroke-dashoffset 900ms ease-out",
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
          }}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fontSize={size * 0.34}
          fill="#fff"
          style={{ fontWeight: 700 }}
        >
          {value}
        </text>
      </svg>
      {label && (
        <div style={{ color: "#fff", fontWeight: 700, lineHeight: 1.1 }}>
          {label}
          <div style={{ fontSize: 12, opacity: 0.85 }}>{pct}%</div>
        </div>
      )}
    </div>
  );
}

function MiniCircle({ value, max }) {
  const size = 28;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const offset = C * (1 - pct / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,.15)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="white"
        strokeLinecap="round"
        strokeWidth={stroke}
        strokeDasharray={`${C} ${C}`}
        strokeDashoffset={offset}
        style={{
          transition: "stroke-dashoffset 900ms ease-out",
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%",
        }}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize={size * 0.42}
        fill="#fff"
        style={{ fontWeight: 700 }}
      >
        {value}
      </text>
    </svg>
  );
}

// kleine Hilfsfunktion zum Anzeigen von km pro Markt
const formatKmLine = (label, value) => {
  const n = Number(value || 0);
  if (!n) return null;
  return (
    <div>
      {label}: {n.toFixed(1)} km
    </div>
  );
};
const formatHeightLine = (label, value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return (
    <div>
      {label}: {Math.round(n)} m
    </div>
  );
};

/** -------------------- Map Component -------------------- **/
export default function Map() {
  const [mapStyle, setMapStyle] = useState("dark");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [adressen, setAdressen] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [year, setYear] = useState("2025");
  const [mode, setMode] = useState("border"); // 'border' | 'flags'

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );
  const [statsOpen, setStatsOpen] = useState(false);

  const mapRef = useRef(null);
  const austriaRef = useRef(null);

  // Responsive
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const h = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", h) : mq.addListener(h);
    return () => {
      mq.removeEventListener
        ? mq.removeEventListener("change", h)
        : mq.removeListener(h);
    };
  }, []);

  // Adressen holen
  useEffect(() => {
    fetch("https://ckm.hoope.me/getAdressen.php")
      .then((r) => r.json())
      .then((data) => {
        setAdressen(data);
        setLoaded(true);
      })
      .catch((e) => console.error("Fehler beim Laden der Adressen:", e));
  }, []);

  // Jahr-Filter (ID-Logik)
  const filteredAdressen = useMemo(() => {
    return adressen.filter((a) => {
      const id = Number(a.id);
      if (Number.isNaN(id)) return false;
      if (year === "2024") return id >= 1 && id < 26;
      if (year === "2025") return id >= 26;
      return true;
    });
  }, [adressen, year]);

  // Statistik inkl. km-, CO₂- und Umwelt-Summen
  const stats = useMemo(() => {
    const byCountry = {};
    const kmTotals = {
      zug: 0,
      ubahn: 0,
      sbahn: 0,
      fuss: 0,
      bus: 0,
      fahrrad: 0, // NEU
      bergbahn: 0, // NEU
      auto: 0,
      schiff: 0,
      flug: 0,
    };

    let heightMin = null;
    let heightMax = null;
    let heightSum = 0;
    let heightCount = 0;
    let heightMinMarket = null;
    let heightMaxMarket = null;

    const envTotals = {
      tassen: 0,
      kinderpunsch: 0,
      schokolade: 0,
      tee: 0,
    };

    for (const a of filteredAdressen) {
      const country =
        (a.land && String(a.land).trim()) || a.land_iso2 || "Unbekannt";
      const region = (a.bundesland && String(a.bundesland).trim()) || null;

      if (!byCountry[country]) byCountry[country] = { total: 0, regions: {} };
      byCountry[country].total += 1;
      if (region)
        byCountry[country].regions[region] =
          (byCountry[country].regions[region] || 0) + 1;

      kmTotals.zug += Number(a.zug_km || 0);
      kmTotals.ubahn += Number(a.ubahn_km || 0);
      kmTotals.sbahn += Number(a.sbahn_km || 0);
      kmTotals.fuss += Number(a.fuss_km || 0);
      kmTotals.bus += Number(a.bus_km || 0);
      kmTotals.auto += Number(a.auto_km || 0);
      kmTotals.schiff += Number(a.schiff_km || 0);
      kmTotals.flug += Number(a.flug_km || 0);
      kmTotals.fahrrad += Number(a.fahrrad_km || 0);
      kmTotals.bergbahn += Number(a.bergbahn_km || 0);

      if (Number(a.tasse) === 1) envTotals.tassen += 1;
      if (Number(a.kinderpunsch) === 1) envTotals.kinderpunsch += 1;
      if (Number(a.heisse_schokolade) === 1) envTotals.schokolade += 1;
      if (Number(a.tee) === 1) envTotals.tee += 1;
      if (a.hoehe != null && !Number.isNaN(Number(a.hoehe))) {
        const h = Number(a.hoehe);

        if (heightMin === null || h < heightMin) {
          heightMin = h;
          heightMinMarket = a;
        }

        if (heightMax === null || h > heightMax) {
          heightMax = h;
          heightMaxMarket = a;
        }
        heightSum += h;
        heightCount++;
      }
    }

    const totalCount = filteredAdressen.length;
    const totalKm =
      kmTotals.zug +
      kmTotals.ubahn +
      kmTotals.sbahn +
      kmTotals.fuss +
      kmTotals.fahrrad + // NEU
      kmTotals.bergbahn + // NEU
      kmTotals.bus +
      kmTotals.auto +
      kmTotals.schiff +
      kmTotals.flug;

    // CO₂ nach Verkehrsart
    const co2ByMode = {
      zug: kmTotals.zug * CO2_FACTORS.zug,
      ubahn: kmTotals.ubahn * CO2_FACTORS.ubahn,
      sbahn: kmTotals.sbahn * CO2_FACTORS.sbahn,
      fuss: kmTotals.fuss * CO2_FACTORS.fuss,
      fahrrad: kmTotals.fahrrad * CO2_FACTORS.fahrrad, // NEU
      bergbahn: kmTotals.bergbahn * CO2_FACTORS.bergbahn, // NEU
      bus: kmTotals.bus * CO2_FACTORS.bus,
      auto: kmTotals.auto * CO2_FACTORS.auto,
      schiff: kmTotals.schiff * CO2_FACTORS.schiff,
      flug: kmTotals.flug * CO2_FACTORS.flug,
    };

    const totalCo2 = Object.values(co2ByMode).reduce((sum, v) => sum + v, 0);

    // Baseline: alles mit Auto gefahren
    const baselineCarCo2 = totalKm * CO2_FACTORS.auto;
    const savedCo2 = baselineCarCo2 - totalCo2;

    const sorted = Object.entries(byCountry)
      .sort(([, A], [, B]) => B.total - A.total)
      .map(([name, v]) => ({
        name,
        total: v.total,
        regions: Object.entries(v.regions)
          .sort(([, a], [, b]) => b - a)
          .map(([r, c]) => ({ name: r, count: c })),
      }));

    const heightAvg = heightCount > 0 ? heightSum / heightCount : null;

    return {
      countries: sorted,
      totalCount,
      kmTotals,
      totalKm,
      co2ByMode,
      totalCo2,
      baselineCarCo2,
      savedCo2,
      envTotals,
      heightMin,
      heightMax,
      heightAvg,
      heightMinMarket,
      heightMaxMarket,
    };
  }, [filteredAdressen]);

  // Re-Apply AUT Füllung
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const reapply = () => applyAustriaFill(austriaRef.current, mode);
    map.on("zoomend viewreset moveend layeradd", reapply);
    reapply();
    return () => map.off("zoomend viewreset moveend layeradd", reapply);
  }, [loaded, mode]);

  // ESC zum Schließen des Bottom-Sheets
  useEffect(() => {
    if (!statsOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setStatsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [statsOpen]);

  // Layer Styles
  const neighborStyle = (feature) => {
    const iso =
      feature?.properties?.ISO_A3 ||
      feature?.properties?.iso_a3 ||
      NAME_TO_ISO[feature?.properties?.name] ||
      "AUT";
    return mode === "flags"
      ? {
          fill: true,
          fillOpacity: 1,
          fillColor: `url(#flag-${iso})`,
          color: "#555",
          weight: 1,
        }
      : { fill: false, fillOpacity: 0, color: "#6c757d", weight: 1 };
  };

  const austriaStyle = () =>
    mode === "flags"
      ? {
          fill: true,
          fillColor: "url(#flag-AUT)",
          fillOpacity: 1,
          color: "#d1182e",
          weight: 1.5,
        }
      : { fill: false, fillOpacity: 0, color: "#d1182e", weight: 1.8 };

  /** ---------- Statistik JSX ---------- */
  const StatsContent = (
    <>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>
        Übersicht {year} – {filteredAdressen.length} Märkte
      </div>

      {/* KM-Statistik + Gesamt + Klima + Umwelt nur für 2025 */}
      {year === "2025" && (
        <>
          {/* KM-Kacheln */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {[
              ["🚆 Zug", stats.kmTotals.zug],
              ["🚇 U-Bahn", stats.kmTotals.ubahn],
              ["🚈 S-Bahn", stats.kmTotals.sbahn],
              ["🚌 Bus", stats.kmTotals.bus],
              ["🚶‍♂️ Zu Fuß", stats.kmTotals.fuss],
              ["🚲 Fahrrad", stats.kmTotals.fahrrad], // NEU
              ["🚠 Bergbahn", stats.kmTotals.bergbahn], // NEU
              ["🚗 Auto", stats.kmTotals.auto],
              ["🚢 Schiff", stats.kmTotals.schiff],
              ["✈️ Flug", stats.kmTotals.flug],
            ].map(([label, km]) => (
              <div
                key={label}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  padding: "10px",
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.8 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>
                  {km.toFixed(1)} km
                </div>
              </div>
            ))}
          </div>

          {/* Gesamt-KM */}
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.85 }}>Gesamtstrecke</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              {stats.totalKm.toFixed(1)} km
            </div>
          </div>

          {stats.heightAvg != null && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 16,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>
                ⛰️ Höhenübersicht
              </div>
              <div
                title={
                  stats.heightMinMarket
                    ? `${stats.heightMinMarket.name}, ${stats.heightMinMarket.stadt}`
                    : ""
                }
                style={{ cursor: "help" }}
              >
                ⬇️ Niedrigster Markt: <b>{Math.round(stats.heightMin)} m</b>
              </div>

              <div
                title={
                  stats.heightMaxMarket
                    ? `${stats.heightMaxMarket.name}, ${stats.heightMaxMarket.stadt}`
                    : ""
                }
                style={{ cursor: "help" }}
              >
                ⬆️ Höchster Markt: <b>{Math.round(stats.heightMax)} m</b>
              </div>

              <div>
                📊 Durchschnitt: <b>{Math.round(stats.heightAvg)} m</b>
              </div>
            </div>
          )}

          {/* Klima-Box */}
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 16,
              background: "rgba(34,197,94,0.15)",
              border: "1px solid rgba(34,197,94,0.6)",
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>
              🌱 Klimabilanz {year}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>
              {stats.totalCo2.toFixed(1)} kg CO₂
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
              Wenn alles mit dem Auto gefahren wäre:{" "}
              {stats.baselineCarCo2.toFixed(1)} kg CO₂
            </div>
            {stats.savedCo2 > 0 && (
              <div
                style={{
                  fontSize: 13,
                  marginTop: 4,
                  fontWeight: 600,
                }}
              >
                ➜ ca. {stats.savedCo2.toFixed(1)} kg CO₂ eingespart 🎉
              </div>
            )}
          </div>

          {/* Tassen & Getränke */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.08)",
                borderRadius: 12,
                padding: "10px",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.8 }}>☕ Tassen</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>
                {stats.envTotals.tassen}
              </div>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.08)",
                borderRadius: 12,
                padding: "10px",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.8 }}>🧃 Kinderpunsch</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>
                {stats.envTotals.kinderpunsch}
              </div>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.08)",
                borderRadius: 12,
                padding: "10px",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                🍫 Heiße Schokolade
              </div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>
                {stats.envTotals.schokolade}
              </div>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.08)",
                borderRadius: 12,
                padding: "10px",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.8 }}>🍵 Tee</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>
                {stats.envTotals.tee}
              </div>
            </div>
          </div>
        </>
      )}

      {stats.countries.length === 0 ? (
        <div style={{ opacity: 0.8 }}>Keine Daten.</div>
      ) : (
        stats.countries.map((c) => (
          <div
            key={c.name}
            style={{
              background: "rgba(255,255,255,0.08)",
              borderRadius: 12,
              padding: "10px",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <CircularProgress
                value={c.total}
                max={stats.totalCount}
                size={64}
                stroke={8}
                label={c.name}
              />
              <div style={{ textAlign: "right", minWidth: 120 }}>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Gesamt</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{c.total}</div>
              </div>
            </div>

            {c.regions.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {c.regions.map((r) => (
                  <div
                    key={r.name}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "28px 1fr auto",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 0",
                      borderBottom: "1px dashed rgba(255,255,255,0.15)",
                    }}
                  >
                    <MiniCircle value={r.count} max={c.total} />
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.name}
                    </div>
                    <div style={{ fontWeight: 700 }}>{r.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </>
  );

  if (!loaded) return <div>Wird geladen...</div>;
  const TILE_STYLES = {
    light: {
      name: "Light",
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "&copy; OpenStreetMap",
    },
    voyager: {
      name: "Blue Sea",
      url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
      attribution: "&copy; CARTO",
    },
    dark: {
      name: "Dark",
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      attribution: "Tiles © Esri",
    },

    satellite: {
      name: "Sat",
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution: "&copy; Esri",
    },
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Desktop-Statistik */}
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            left: 50,
            top: 50,
            zIndex: 1000,
            maxHeight: "80vh",
            overflow: "auto",
            background: "rgba(0,0,0,0.55)",
            color: "#fff",
            padding: "10px 12px",
            borderRadius: 12,
            backdropFilter: "blur(4px)",
            minWidth: 300,
          }}
        >
          {StatsContent}
        </div>
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <button
          aria-expanded={statsOpen}
          aria-controls="stats-drawer"
          onClick={() => setStatsOpen((v) => !v)}
          style={{
            position: "absolute",
            right: 16,
            bottom: 70,
            zIndex: 1100,
            width: 56,
            height: 56,
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            fontWeight: 800,
            color: "#000",
            background: "#ffd166",
            boxShadow: "0 8px 24px rgba(0,0,0,.35)",
          }}
          title="Statistik"
        >
          ≡
        </button>
      )}

      {/* Mobile Bottom-Sheet */}
      {isMobile && (
        <>
          <div
            onClick={() => setStatsOpen(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              opacity: statsOpen ? 1 : 0,
              pointerEvents: statsOpen ? "auto" : "none",
              transition: "opacity 200ms ease",
              zIndex: 1090,
            }}
          />
          <div
            id="stats-drawer"
            role="dialog"
            aria-label="Statistik"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 70,
              zIndex: 1100,
              background: "rgba(25,25,25,0.92)",
              backdropFilter: "blur(6px)",
              color: "#fff",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              boxShadow: "0 -12px 30px rgba(0,0,0,.5)",
              transform: statsOpen ? "translateY(0%)" : "translateY(100%)",
              transition: "transform 260ms ease-out",
              maxHeight: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 999,
                  background: "rgba(255,255,255,.25)",
                  margin: "6px auto 8px auto",
                }}
              />
              <button
                onClick={() => setStatsOpen(false)}
                style={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,.25)",
                  background: "transparent",
                  color: "#fff",
                  fontSize: 18,
                }}
                aria-label="Schließen"
                title="Schließen"
              >
                ×
              </button>
            </div>
            <div style={{ padding: "0 12px 12px", overflowY: "auto" }}>
              {StatsContent}
            </div>
          </div>
        </>
      )}

      {/* Controls rechts oben */}
      <div
        style={{
          position: "absolute",
          right: 12,
          top: 12,
          zIndex: 1000,
          display: "flex",
          gap: 8,
          background: "rgba(0,0,0,0.55)",
          padding: "6px 8px",
          borderRadius: 12,
          backdropFilter: "blur(4px)",
        }}
      >
        {["2024", "2025"].map((y) => (
          <button
            key={y}
            onClick={() => setYear(y)}
            style={{
              border: "none",
              padding: "6px 10px",
              borderRadius: 10,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              color: year === y ? "#000" : "#fff",
              background: year === y ? "#77e36a" : "transparent",
              outline: "1px solid rgba(255,255,255,0.4)",
            }}
          >
            {y}
          </button>
        ))}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setDropdownOpen((v) => !v)}
            style={{
              border: "none",
              padding: "6px 10px",
              borderRadius: 10,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              color: "#000",
              background: "#e0e0e0",
              outline: "1px solid rgba(255,255,255,0.4)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            🗺️ {TILE_STYLES[mapStyle].name} ▼
          </button>

          {dropdownOpen && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: "110%",
                background: "rgba(0,0,0,0.75)",
                backdropFilter: "blur(6px)",
                borderRadius: 10,
                padding: "6px 0",
                minWidth: 120,
                zIndex: 2000,
                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
              }}
            >
              {Object.entries(TILE_STYLES).map(([key, style]) => (
                <div
                  key={key}
                  onClick={() => {
                    setMapStyle(key);
                    setDropdownOpen(false);
                  }}
                  style={{
                    padding: "6px 12px",
                    cursor: "pointer",
                    color: "#fff",
                    background:
                      key === mapStyle
                        ? "rgba(255,255,255,0.15)"
                        : "transparent",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {style.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => setMode((m) => (m === "border" ? "flags" : "border"))}
          style={{
            border: "none",
            padding: "6px 10px",
            borderRadius: 10,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 14,
            color: "#000",
            background: mode === "flags" ? "#ffd166" : "#e0e0e0",
            outline: "1px solid rgba(255,255,255,0.4)",
          }}
          title={mode === "flags" ? "Flaggen aus" : "Flaggen an"}
        >
          {mode === "flags" ? "🇦🇹" : "◇"}
        </button>
      </div>

      {/* Karte */}
      <MapContainer
        ref={mapRef}
        whenCreated={(map) => {
          mapRef.current = map;
          console.log("MapContainer whenCreated → Leaflet-Map ist ready:", map);
        }}
        center={[47.5162, 13.5501]}
        zoom={6}
        style={{ height: "100vh", width: "100%" }}
      >
        <UseCountryGradients />

        <TileLayer
          url={TILE_STYLES[mapStyle].url}
          attribution={TILE_STYLES[mapStyle].attribution}
        />

        <GeoJSON
          key={`neighbors-${mode}`}
          data={neighborsData}
          style={neighborStyle}
          onEachFeature={(feature, layer) => {
            const name =
              feature?.properties?.ADMIN ||
              feature?.properties?.name ||
              feature?.properties?.NAME ||
              "Land";
            layer.bindPopup(`<b>${name}</b>`);
          }}
        />

        <GeoJSON
          key={`austria-${mode}`}
          ref={austriaRef}
          data={geoData}
          style={austriaStyle}
          onEachFeature={(_, layer) => {
            layer.bindPopup("<b>Österreich</b>");
            layer.once("add", () => applyAustriaFill(austriaRef.current, mode));
          }}
        />

        {filteredAdressen.map((a) => (
          <Marker
            key={a.id}
            position={[Number(a.lat), Number(a.lng)]}
            icon={makeEmojiIcon(30)}
          >
            <Popup>
              <b>{a.name}</b>
              <br />
              {a.strasse}, {a.plz} {a.stadt}
              <br />
              <small>
                {a.land ? `Land: ${a.land}` : ""}
                {a.bundesland ? ` · Region: ${a.bundesland}` : ""}
              </small>
              <br />
              <small>Jahr: {year}</small>
              <br />
              <br />
              {/* km + Umweltinfos pro Markt nur für 2025 */}
              {year === "2025" && (
                <>
                  {formatKmLine("🚆 Zug", a.zug_km)}
                  {formatKmLine("🚇 U-Bahn", a.ubahn_km)}
                  {formatKmLine("🚈 S-Bahn", a.sbahn_km)}
                  {formatKmLine("🚌 Bus", a.bus_km)}
                  {formatKmLine("🚶‍♂️ Zu Fuß", a.fuss_km)}
                  {formatKmLine("🚗 Auto", a.auto_km)}
                  {formatKmLine("🚢 Schiff", a.schiff_km)}
                  {formatKmLine("✈️ Flug", a.flug_km)}
                  {formatKmLine("🚲 Fahrrad", a.fahrrad_km)}
                  {formatKmLine("🚠 Bergbahn", a.bergbahn_km)}

                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    {Number(a.tasse) === 1 && (
                      <div>☕ Tassen / Mehrwegbecher vorhanden</div>
                    )}
                    {Number(a.kinderpunsch) === 1 && <div>🧃 Kinderpunsch</div>}
                    {Number(a.heisse_schokolade) === 1 && (
                      <div>🍫 Heiße Schokolade</div>
                    )}
                    {Number(a.tee) === 1 && <div>🍵 Tee</div>}
                  </div>
                </>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
